import React, { useCallback, useEffect, useState } from 'react';
import { Button, Modal, Form, Spinner, Badge } from 'react-bootstrap';
import { getFormulaConfigs, upsertFormulaConfig, deleteFormulaConfig } from '../../services/recommendationsApi';

const DEFAULT_FORMULA = 'ceil((100 / primaryPct) * constant * portionPct - (secondaryPct / 100) * dependentAmt * 2.17)';

const FERTILIZER_TYPE_OPTIONS = [
  { value: 'urea',                         label: 'Urea (standard)' },
  { value: 'urea_10_26_26',                label: 'Urea with 10-26-26 (75%)' },
  { value: 'ammonium_sulphate_10_26_26',   label: 'Ammonium Sulphate with 10-26-26 (25%)' },
  { value: 'calcium_nitrate_ca',           label: 'Calcium Nitrate for Ca' },
  { value: 'mg_sulphate_mg',               label: 'Magnesium Sulphate for Mg' },
];

const DEPENDENT_ON_OPTIONS = [
  { value: 'dap',              label: 'DAP (Phosphorus fertilizer)' },
  { value: '10_26_26',         label: '10-26-26 fertilizer' },
  { value: 'calcium_nitrate',  label: 'Calcium Nitrate (from N)' },
  { value: 'mg_sulphate',      label: 'Magnesium Sulphate (from S)' },
];

const emptyForm = () => ({
  fertilizer_type: 'urea',
  custom_fertilizer_type: '',
  constant_low: '',
  constant_medium: '',
  constant_high: '',
  primary_nutrient_pct: 46,
  secondary_nutrient_pct: 18,
  portion_pct: 1,
  dependent_on: 'dap',
  formula: DEFAULT_FORMULA,
  dose_splits_input: '0.5, 0.25, 0.25',
  dose_is_array: true,
});

const splitsToInput = (splits) => {
  if (!Array.isArray(splits)) return '0.5';
  return splits.map(s => parseFloat(s)).join(', ');
};

const inputToSplits = (str) => {
  return str.split(',').map(s => parseFloat(s.trim())).filter(n => !isNaN(n));
};

const CropFormulaConfigs = ({ slug, viewMode }) => {
  const [configs, setConfigs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [message, setMessage] = useState('');


  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getFormulaConfigs(slug);
      setConfigs(data);
    } catch (e) {
      setMessage(e.response?.data?.message || e.message || 'Failed to load formula configs');
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm());
    setMessage('');
    setShowModal(true);
  };

  const openEdit = (cfg) => {
    setEditing(cfg);
    setForm({
      fertilizer_type: cfg.fertilizer_type,
      constant_low: cfg.constant_low ?? '',
      constant_medium: cfg.constant_medium ?? '',
      constant_high: cfg.constant_high ?? '',
      primary_nutrient_pct: cfg.primary_nutrient_pct,
      secondary_nutrient_pct: cfg.secondary_nutrient_pct,
      portion_pct: cfg.portion_pct,
      dependent_on: cfg.dependent_on || 'dap',
      formula: cfg.formula || DEFAULT_FORMULA,
      dose_splits_input: splitsToInput(cfg.dose_splits),
      dose_is_array: !!cfg.dose_is_array,
    });
    setMessage('');
    setShowModal(true);
  };

  const handleSave = async () => {
    setSaving(true);
    setMessage('');
    try {
      const actualType = form.fertilizer_type === 'custom'
        ? form.custom_fertilizer_type.trim()
        : form.fertilizer_type;
      if (!actualType) {
        setMessage('Please enter a custom fertilizer type name');
        setSaving(false);
        return;
      }
      const splits = inputToSplits(form.dose_splits_input);
      if (splits.length === 0) {
        setMessage('Dose splits must have at least one value');
        setSaving(false);
        return;
      }
      await upsertFormulaConfig(slug, actualType, {
        constant_low:           form.constant_low   !== '' ? parseFloat(form.constant_low)   : null,
        constant_medium:        form.constant_medium !== '' ? parseFloat(form.constant_medium) : null,
        constant_high:          form.constant_high  !== '' ? parseFloat(form.constant_high)  : null,
        primary_nutrient_pct:   parseFloat(form.primary_nutrient_pct),
        secondary_nutrient_pct: parseFloat(form.secondary_nutrient_pct),
        portion_pct:            parseFloat(form.portion_pct),
        dependent_on:           form.dependent_on,
        formula:                form.formula,
        dose_splits:            splits,
        dose_is_array:          form.dose_is_array,
      });
      setShowModal(false);
      await load();
    } catch (e) {
      setMessage(e.response?.data?.message || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (fertilizerType) => {
    if (!window.confirm(`Delete formula config for "${fertilizerType}"?`)) return;
    setDeleting(fertilizerType);
    try {
      await deleteFormulaConfig(slug, fertilizerType);
      await load();
    } catch (e) {
      setMessage(e.response?.data?.message || e.message || 'Delete failed');
    } finally {
      setDeleting(null);
    }
  };

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));

  const fertilizerLabel = (type) =>
    FERTILIZER_TYPE_OPTIONS.find(o => o.value === type)?.label || type;

  const getConstantLabel = () => {
    const type = form.fertilizer_type === 'custom' ? form.custom_fertilizer_type : form.fertilizer_type;
    if (!type) return 'constant';
    if (type.startsWith('urea')) return 'Constant(Urea Requirement)';
    if (type.startsWith('ammonium_sulphate')) return 'Constant(Ammonium Sulphate Requirement)';
    if (type.includes('calcium')) return 'Constant(Calcium Requirement)';
    if (type.includes('mg_sulphate')) return 'Constant(Magnesium Requirement)';
    return `Constant(${type})`;
  };

  const getDependentLabel = () => {
    const option = DEPENDENT_ON_OPTIONS.find(o => o.value === form.dependent_on);
    return option ? `${option.value.toUpperCase()}Amt` : 'dependentAmt';
  };

  const previewFormula = (form.formula || '')
    .replace(/primaryPct/g, form.primary_nutrient_pct || 'primaryPct')
    .replace(/portionPct/g, form.portion_pct || 'portionPct')
    .replace(/secondaryPct/g, form.secondary_nutrient_pct || 'secondaryPct')
    .replace(/constant/g, getConstantLabel())
    .replace(/dependentAmt/g, getDependentLabel());

  if (loading) {
    return <div className="py-4 text-center"><Spinner animation="border" size="sm" /></div>;
  }

  return (
    <div>
      {message && <div className="alert alert-danger py-2">{message}</div>}

      <div className="d-flex justify-content-between align-items-center mb-2">
        <div>
          <h6 className="mb-0 fw-semibold">Fertilizer Formula Configs</h6>
        </div>
        {!viewMode && (
          <Button size="sm" variant="primary" onClick={openAdd}>
            + Add Config
          </Button>
        )}
      </div>

      <div className="alert alert-light py-2 mb-3" style={{ fontSize: '0.78rem' }}>
        <strong>Fertilizer type → nutrient parameter mapping:</strong>
        <ul className="mb-0 mt-1 ps-3">
          <li><code>urea</code> — N (standard, single fertilizer path)</li>
          <li><code>urea_10_26_26</code> + <code>ammonium_sulphate_10_26_26</code> — N (10-26-26 split pattern; both must be configured together)</li>
          <li><code>calcium_nitrate_ca</code> — Ca (depends on N's Calcium Nitrate dose, set via <em>Dependent On</em> = calcium_nitrate)</li>
          <li><code>mg_sulphate_mg</code> — Mg (depends on S's Magnesium Sulphate dose, set via <em>Dependent On</em> = mg_sulphate)</li>
        </ul>
      </div>

      {configs.length === 0 ? (
        <div className="text-muted py-3">
          No formula configs defined for this crop. When the feature flag is enabled, the
          hardcoded switch/case logic will still run as a fallback.
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-bordered table-sm align-middle">
            <thead className="table-light">
              <tr>
                <th>Fertilizer Type</th>
                <th>Constants (Low / Med / High)</th>
                <th>Nutrient %</th>
                <th>Portion</th>
                <th>Dependent On</th>
                <th>Dose Splits</th>
                <th>Array?</th>
                {!viewMode && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {configs.map((cfg) => (
                <tr key={cfg.fertilizer_type}>
                  <td>
                    <span className="fw-semibold">{fertilizerLabel(cfg.fertilizer_type)}</span>
                    <br />
                    <code className="text-muted" style={{ fontSize: '0.75rem' }}>{cfg.fertilizer_type}</code>
                  </td>
                  <td>
                    <span className="text-success">{cfg.constant_low ?? '—'}</span>
                    {' / '}
                    <span className="text-warning">{cfg.constant_medium ?? '—'}</span>
                    {' / '}
                    <span className={cfg.constant_high == null ? 'text-muted fst-italic' : 'text-danger'}>
                      {cfg.constant_high ?? 'null'}
                    </span>
                  </td>
                  <td>
                    <small>
                      Primary: <strong>{cfg.primary_nutrient_pct}%</strong><br />
                      Secondary: <strong>{cfg.secondary_nutrient_pct}%</strong>
                    </small>
                  </td>
                  <td>{(parseFloat(cfg.portion_pct) * 100).toFixed(0)}%</td>
                  <td>
                    <code style={{ fontSize: '0.75rem' }}>{cfg.dependent_on}</code>
                  </td>
                  <td>
                    <code style={{ fontSize: '0.75rem' }}>
                      {Array.isArray(cfg.dose_splits) ? cfg.dose_splits.join(', ') : '—'}
                    </code>
                  </td>
                  <td>
                    <Badge bg={cfg.dose_is_array ? 'success' : 'secondary'}>
                      {cfg.dose_is_array ? 'Yes' : 'No'}
                    </Badge>
                  </td>
                  {!viewMode && (
                    <td>
                      <Button size="sm" variant="outline-secondary" className="me-1" onClick={() => openEdit(cfg)}>
                        Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline-danger"
                        disabled={deleting === cfg.fertilizer_type}
                        onClick={() => handleDelete(cfg.fertilizer_type)}
                      >
                        {deleting === cfg.fertilizer_type ? <Spinner animation="border" size="sm" /> : 'Delete'}
                      </Button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editing ? 'Edit Formula Config' : 'Add Formula Config'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {message && <div className="alert alert-danger py-2">{message}</div>}

          <div className="row g-3">
            <div className="col-md-6">
              <Form.Label className="fw-semibold">Fertilizer Type</Form.Label>
              {editing ? (
                <Form.Control value={form.fertilizer_type} disabled />
              ) : (
                <>
                  <Form.Select value={form.fertilizer_type} onChange={e => set('fertilizer_type', e.target.value)}>
                    {FERTILIZER_TYPE_OPTIONS.map(o => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                    <option value="custom">Custom…</option>
                  </Form.Select>
                  {form.fertilizer_type === 'custom' && (
                    <Form.Control
                      className="mt-1"
                      placeholder="e.g. my_custom_fertilizer"
                      value={form.custom_fertilizer_type}
                      onChange={e => set('custom_fertilizer_type', e.target.value)}
                    />
                  )}
                </>
              )}
            </div>

            <div className="col-md-6">
              <Form.Label className="fw-semibold">Dependent On</Form.Label>
              <Form.Select value={form.dependent_on} onChange={e => set('dependent_on', e.target.value)}>
                {DEPENDENT_ON_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </Form.Select>
            </div>

            <div className="col-md-4">
              <Form.Label>Constant — Low</Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g. 71.25"
                value={form.constant_low}
                onChange={e => set('constant_low', e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <Form.Label>Constant — Medium</Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g. 57"
                value={form.constant_medium}
                onChange={e => set('constant_medium', e.target.value)}
              />
            </div>
            <div className="col-md-4">
              <Form.Label>
                Constant — High{' '}
                <small className="text-muted">(leave blank = no recommendation at high)</small>
              </Form.Label>
              <Form.Control
                type="number"
                placeholder="null = skip"
                value={form.constant_high}
                onChange={e => set('constant_high', e.target.value)}
              />
            </div>

            <div className="col-md-4">
              <Form.Label>Primary Nutrient %</Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g. 46"
                value={form.primary_nutrient_pct}
                onChange={e => set('primary_nutrient_pct', e.target.value)}
              />
              <Form.Text className="text-muted">46=Urea-N, 19=CaNO₃-Ca, 9.6=MgSO₄-Mg</Form.Text>
            </div>
            <div className="col-md-4">
              <Form.Label>Secondary Nutrient %</Form.Label>
              <Form.Control
                type="number"
                placeholder="e.g. 18"
                value={form.secondary_nutrient_pct}
                onChange={e => set('secondary_nutrient_pct', e.target.value)}
              />
              <Form.Text className="text-muted">18=DAP, 10=10-26-26, 19=CaNO₃, 9.6=MgSO₄</Form.Text>
            </div>
            <div className="col-md-4">
              <Form.Label>Portion (fraction of N)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                placeholder="e.g. 1 or 0.75"
                value={form.portion_pct}
                onChange={e => set('portion_pct', e.target.value)}
              />
              <Form.Text className="text-muted">1=100%, 0.75=75%, 0.25=25%</Form.Text>
            </div>

            <div className="col-12">
              <Form.Label className="fw-semibold">
                Formula{' '}
                <small className="text-muted fw-normal">
                  (mathjs expression — variables: primaryPct, constant, portionPct, secondaryPct, dependentAmt)
                </small>
              </Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={form.formula}
                onChange={e => set('formula', e.target.value)}
                style={{ fontFamily: 'monospace', fontSize: '0.85rem' }}
              />
            </div>

            <div className="col-md-8">
              <Form.Label>
                Dose Splits{' '}
                <small className="text-muted">(comma-separated fractions, e.g. 0.5, 0.25, 0.25)</small>
              </Form.Label>
              <Form.Control
                value={form.dose_splits_input}
                onChange={e => set('dose_splits_input', e.target.value)}
                placeholder="0.5, 0.25, 0.25"
              />
            </div>
            <div className="col-md-4 d-flex align-items-end pb-1">
              <Form.Check
                type="switch"
                id="dose-is-array"
                label="Return as array (multi-dose)"
                checked={form.dose_is_array}
                onChange={e => set('dose_is_array', e.target.checked)}
              />
            </div>

            <div className="col-12">
              <div className="alert alert-light py-2 mb-0" style={{ fontSize: '0.8rem' }}>
                <strong>Preview formula logic:</strong>{' '}
                <code>{previewFormula}</code>
                <br />
                <strong>Dose splits:</strong>{' '}
                {inputToSplits(form.dose_splits_input).map((s, i) => (
                  <Badge key={i} bg="secondary" className="me-1">{(s * 100).toFixed(0)}%</Badge>
                ))}
                {form.dose_is_array ? ' → array' : ' → single value (first split used)'}
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>Cancel</Button>
          <Button variant="primary" onClick={handleSave} disabled={saving}>
            {saving ? <><Spinner animation="border" size="sm" /> Saving…</> : 'Save'}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default CropFormulaConfigs;